const DEFAULT_FIXED_PREFIX = 2;

/**
 * Build display headers from full header list and saved config.
 */
export function resolveTableHeaders(allHeaders, config, fixedPrefixCount = DEFAULT_FIXED_PREFIX) {
  if (!allHeaders?.length) return [];

  const fixed = allHeaders.slice(0, fixedPrefixCount);
  const dataHeaders = allHeaders.slice(fixedPrefixCount);

  const order = Array.isArray(config?.columnOrder)
    ? config.columnOrder.filter((h) => dataHeaders.includes(h))
    : [];

  const visibleSet = Array.isArray(config?.visibleColumns)
    ? new Set(config.visibleColumns)
    : null;

  const orderedData = [];
  order.forEach((h) => {
    if (!visibleSet || visibleSet.has(h)) orderedData.push(h);
  });

  dataHeaders.forEach((h) => {
    if (orderedData.includes(h)) return;
    if (!visibleSet || visibleSet.has(h)) orderedData.push(h);
  });

  if (!orderedData.length && !visibleSet) return [...allHeaders];
  if (!orderedData.length) return [...fixed, ...dataHeaders];

  return [...fixed, ...orderedData];
}

export function getDataHeaders(headers, fixedPrefixCount = DEFAULT_FIXED_PREFIX) {
  return headers.slice(fixedPrefixCount);
}

export function getStickyColumnIndices(headers, stickyColumnKeys, fallbackIndices = [0, 1, 2]) {
  if (!headers?.length) return fallbackIndices;

  if (Array.isArray(stickyColumnKeys) && stickyColumnKeys.length) {
    const indices = stickyColumnKeys
      .map((key) => headers.indexOf(key))
      .filter((i) => i >= 0);
    if (indices.length) return indices;
  }

  return fallbackIndices.filter((i) => i < headers.length);
}

/**
 * Build saved-view config from TanStack Table column state (order, visibility, pinning).
 */
export function buildViewConfigFromTableLayout({
  allHeaders,
  columnOrder = [],
  columnVisibility = {},
  columnPinning = {},
  columnWidths = {},
  defaultStickyColumnKeys = [],
  fixedPrefixCount = DEFAULT_FIXED_PREFIX,
  ...rest
}) {
  if (!allHeaders?.length) return buildViewConfig({ headers: [], columnWidths, ...rest });

  const fixed = allHeaders.slice(0, fixedPrefixCount);
  const dataHeaderSet = new Set(allHeaders.slice(fixedPrefixCount));
  const orderSource = columnOrder.length ? columnOrder : allHeaders;

  const orderedVisibleData = orderSource
    .filter((id) => dataHeaderSet.has(id))
    .filter((id) => columnVisibility[id] !== false);

  const headers = [...fixed, ...orderedVisibleData];
  const stickyColumns =
    columnPinning?.left?.length > 0
      ? columnPinning.left
      : defaultStickyColumnKeys;

  return buildViewConfig({
    headers,
    columnWidths,
    stickyColumns,
    fixedPrefixCount,
    ...rest,
  });
}

export function buildViewConfig({
  headers,
  columnWidths = {},
  stickyColumns,
  sortBy,
  ascending,
  filter,
  advancedFilters,
  currentPage,
  pageSize,
  fixedPrefixCount = DEFAULT_FIXED_PREFIX,
}) {
  const dataHeaders = getDataHeaders(headers, fixedPrefixCount);

  return {
    visibleColumns: dataHeaders,
    columnOrder: headers,
    columnWidths,
    stickyColumns: stickyColumns || headers.slice(0, Math.min(3, headers.length)),
    sortBy: sortBy ?? null,
    ascending: ascending ?? false,
    filter: filter ?? null,
    advancedFilters: advancedFilters ?? null,
    currentPage: currentPage ?? 1,
    pageSize: pageSize ?? null,
  };
}

const arraysEqual = (a, b) =>
  a === b ||
  (Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((value, index) => value === b[index]));

const jsonEqual = (a, b) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Compare two saved-view configs for equality (used to derive dirty state).
 */
export function viewConfigsMatch(saved, current) {
  if (!saved || !current) return saved === current;

  if (saved.filter !== undefined && (saved.filter ?? null) !== (current.filter ?? null)) {
    return false;
  }

  if (
    saved.advancedFilters !== undefined &&
    !jsonEqual(saved.advancedFilters, current.advancedFilters)
  ) {
    return false;
  }

  return (
    arraysEqual(saved.visibleColumns, current.visibleColumns) &&
    arraysEqual(saved.columnOrder, current.columnOrder) &&
    jsonEqual(saved.columnWidths, current.columnWidths) &&
    arraysEqual(saved.stickyColumns, current.stickyColumns) &&
    (saved.sortBy ?? null) === (current.sortBy ?? null) &&
    Boolean(saved.ascending) === Boolean(current.ascending) &&
    (saved.currentPage ?? 1) === (current.currentPage ?? 1) &&
    (saved.pageSize ?? null) === (current.pageSize ?? null)
  );
}

export function pickInitialView(views = []) {
  if (!views.length) return null;

  // Explicit default wins for login / cold start. Session resume is handled by
  // localStorage in loadViews (including the System Default sentinel).
  const defaultView = views.find((v) => v.isDefault);
  if (defaultView) return defaultView;

  // No marked default → System Default (do not revive lastUsedAt here; that
  // made System Default impossible to keep across login when localStorage
  // preference was missing).
  return null;
}

/** Sentinel stored when the user explicitly chooses System Default. */
export const SYSTEM_DEFAULT_VIEW_ID = "__system_default__";

const LAST_USED_KEY_PREFIX = "tableSavedView:lastUsed:";

export function lastUsedStorageKey(pageKey) {
  return `${LAST_USED_KEY_PREFIX}${pageKey}`;
}

/**
 * Clear every page's "last used view" preference. This key otherwise survives
 * logout (it lives in localStorage, not the auth session), so without this a
 * server-side default set via "Set as default" is masked on next login by
 * whatever view was last open before logging out. Call on logout/session end.
 */
export function clearAllLastUsedViewPreferences() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LAST_USED_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

export function isSystemDefaultPreference(viewId) {
  return viewId === SYSTEM_DEFAULT_VIEW_ID;
}

export function readLastUsedViewId(pageKey) {
  try {
    const raw = localStorage.getItem(lastUsedStorageKey(pageKey));
    return raw != null && raw !== "" ? raw : null;
  } catch {
    return null;
  }
}

export function writeLastUsedViewId(pageKey, viewId) {
  try {
    if (viewId == null) {
      // Persist an explicit System Default preference. Clearing the key would
      // make reload fall through to pickInitialView (server lastUsedAt) and
      // re-select a user-created view.
      localStorage.setItem(lastUsedStorageKey(pageKey), SYSTEM_DEFAULT_VIEW_ID);
    } else {
      localStorage.setItem(lastUsedStorageKey(pageKey), String(viewId));
    }
  } catch {
    /* ignore */
  }
}
